import React from 'react';
import { useSelector } from 'react-redux';
import { DataCard, Select, Icon } from '@brighterion/br-ui';
import { COMPARE_SORT_VIEW, SIMULATION_FILTER, SORT_VIEW } from 'Common/enums';
import { useTranslation, Trans } from 'react-i18next';
import { getUser } from 'Selectors';
import useGetLatestRepositoryConfig from 'Hooks/query-hooks/repository/useGetLatestRepositoryConfig';
import { DateService } from 'Services';
import { getCustomUserName } from 'Utils';
import clsx from 'clsx';
import styles from '../../ViewSimulation.css';

const sortViewSelect = (
    sortView: SortType,
    setSortView: (value: SortType) => void,
    t: (text: string, options?: any) => string,
    size?: 'sm' | 'lg',
    compare?: string[]
) => (
    <Select
        className={styles.sortViewSelectContainer}
        labelClassName={styles.sortViewSelectLabel}
        label={t('Sort')}
        handleClickCallback={(value) => setSortView(value[0])}
        placeholder='-Select-'
        selectedItems={[sortView]}
        inlineLabel
        size={size}
    >
        {Object.entries(compare?.length ? COMPARE_SORT_VIEW : SORT_VIEW).map(([key, value]) => (
            <option key={key} value={value}>
                {t(value)}
            </option>
        ))}
    </Select>
);

const filterViewSelect = (
    filterView: FilterViewOptions,
    setFilterView: (value: FilterViewOptions) => void,
    t: (text: string, options?: any) => string,
    size?: 'sm' | 'lg'
) => (
    <Select
        className={styles.filterViewSelectContainer}
        labelClassName={styles.filterViewSelectLabel}
        label={t('Filter')}
        handleClickCallback={(value) => setFilterView(value[0])}
        placeholder='-Select-'
        selectedItems={[filterView]}
        inlineLabel
        size={size}
    >
        <option value='All'>{t(SIMULATION_FILTER.ALL)}</option>
        <option value='Modified'>{t(SIMULATION_FILTER.MODIFIED)}</option>
        <option value='New'>{t(SIMULATION_FILTER.NEW)}</option>
    </Select>
);

type Props = {
    formValues: ProcessedSimulationReportFormValues;
    sortView: SortType;
    setSortView: (value: SortType) => void;
    filterView: FilterViewOptions;
    setFilterView: (value: FilterViewOptions) => void;
    newlyGenerated?: boolean;
    totalTransactions: string;
    users?: UsersHashmap;
    deployedVersion?: number;
    compare?: string[];
    hideMerchantDataCard?: boolean;
    hideCompareDataCard?: boolean;
};

const HeaderRows = ({
    formValues,
    sortView,
    setSortView,
    filterView,
    setFilterView,
    newlyGenerated,
    totalTransactions,
    users,
    deployedVersion,
    compare,
    hideMerchantDataCard,
    hideCompareDataCard
}: Props) => {
    const { t } = useTranslation();
    const { userId, username } = useSelector(getUser);
    const { start, end, createdBy, createdAt, currencyEffectiveDate: existingCurrencyEffectiveDate } = formValues;
    const repositoryConfig = useGetLatestRepositoryConfig();

    const currencyEffectiveDate = DateService.getDateTime(
        existingCurrencyEffectiveDate || repositoryConfig.data?.currencyEffectiveDate
    );

    const renderCreatedBy = () => {
        // if the current user is found from users_info table in DB
        // display username from DB, otherwise, display username from mcconnect / okta
        if (newlyGenerated) return `${getCustomUserName(userId, users, username)} (${t('me')})`;

        // If the current user is not the creator of the current report and is found from users_info table in DB
        // display username from DB, else display username from the report endpoint
        const reportCreator = getCustomUserName(createdBy, users);
        return userId === createdBy ? `${reportCreator} (${t('me')})` : `${reportCreator}`;
    };

    const reportHeader = (
        <>
            <Trans i18nKey='Sandbox evalulated'>
                WTF <span className={styles.ruleTotalTransaction}>{{ totalTransactions }}</span> transactions evaluated.
                Percent rounded to the nearest 100th.
            </Trans>
            <div className={styles.ruleDescriptions}>
                <span className={styles.modifiedRule}>
                    <Icon name='table-updated' size='md' />{' '}
                    <span>
                        {t('since Deployment Version', { rule_type: 'Rules Modified', version: deployedVersion })}
                    </span>
                </span>
                <span className={styles.newRule}>
                    <Icon name='table-new' size='md' />{' '}
                    <span>{t('since Deployment Version', { rule_type: 'New Rules', version: deployedVersion })}</span>
                </span>
            </div>
        </>
    );

    return (
        <>
            <div className={styles.inlineHeaderRowWrapper}>
                <div className={clsx(styles.flexRow, styles.inlineReportInfoWrapper)}>
                    <DataCard
                        dataTestId='report-merchantType-card'
                        className={clsx(styles.reportCard)}
                        key='reportType'
                        data={t('Rule Performance')}
                        label={t('Simulation Type')}
                    />
                    <DataCard
                        dataTestId='simulation-sandbox-card'
                        className={clsx(styles.reportCard)}
                        key='sandbox'
                        data={t('All Rules')}
                        label={t('Rules')}
                    />
                    <DataCard
                        dataTestId='simulation-deployment-card'
                        className={clsx(styles.reportCard)}
                        key='deployment'
                        data={deployedVersion ? t('Version', { version: deployedVersion }) : '-'}
                        label={t('Deployment')}
                    />
                    {!hideCompareDataCard && (
                        <DataCard
                            dataTestId='simulation-compare-card'
                            className={clsx(styles.reportCard)}
                            key='compare'
                            data={t('CompareFlag', { compare: compare?.length ? t('Yes') : '-' })}
                            label={t('Compare')}
                        />
                    )}
                    {!hideMerchantDataCard && (
                        <DataCard
                            dataTestId='report-reportType-card'
                            className={clsx(styles.reportCard)}
                            key='merchantType'
                            data={t('All Merchants')}
                            label={t('Subject')}
                        />
                    )}
                    <DataCard
                        dataTestId='report-dateRange-card'
                        className={clsx(styles.reportCard)}
                        key='dateRangeType'
                        data={DateService.getDate(start)}
                        label={t('Start Date')}
                    />
                    <DataCard
                        dataTestId='report-dates-card'
                        className={clsx(styles.reportCard)}
                        key='duration'
                        data={DateService.getDuration(start, end, true)}
                        label={t('Duration')}
                    />
                    <DataCard
                        dataTestId='report-createdAt-card'
                        className={clsx(styles.reportCard)}
                        key='created'
                        data={DateService.getDateTime(createdAt)}
                        label={newlyGenerated ? t('Generated') : t('Created')}
                    />
                    <DataCard
                        dataTestId='report-createdBy-card'
                        className={clsx(styles.reportCard)}
                        key='createdBy'
                        data={renderCreatedBy()}
                        label={newlyGenerated ? t('Generated By') : t('Created By')}
                    />

                    <DataCard
                        dataTestId='currency-effective-date-card'
                        className={clsx(styles.reportCard)}
                        key='currencyEffectiveDate'
                        data={currencyEffectiveDate}
                        label={t('Currency Effective Date')}
                    />
                </div>
            </div>
            <div className={styles.inlineHeaderRowWrapper}>
                <span className={styles.roundedMsg}>{reportHeader}</span>
                <span className={styles.selectViewBoxes}>
                    <span className={styles.filterViewSelectContainer}>
                        {filterViewSelect(filterView, setFilterView, t, 'sm')}
                    </span>
                    <span className={styles.sortViewSelectContainer}>
                        {sortViewSelect(sortView, setSortView, t, 'sm', compare)}
                    </span>
                </span>
            </div>
        </>
    );
};

export default HeaderRows;
